/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {ActionInstance} from "../../../models/action-instance";
import {Dialog, Level} from "../../../models/dialog";
import {DialogButton} from "../../../models/dialog-button";
import {DialogItem} from "../../../models/dialog-item";
import {Event} from "../../../models/event";
import {InstanceData} from "../../../models/instance-data";
import {DialogService} from "../../../services/dialog.service";
import {Component, Input, OnInit, ViewChild} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {MapOptions} from "app/models/map-options";
import {ObjectService} from "app/services/object.service";
import {PropertyType} from "app/models/property-type";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";

declare var $: any;

const PROP_DIALOGNAME = "dialog_name";

@Component({
  selector: 'app-dialog-action-editor',
  templateUrl: './dialog-action-editor.component.html'
})
export class DialogActionEditorComponent implements OnInit {

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  //Tied to the "mEvent" Event Variable.  Holds the current Event tied to the Rule / Action.
  _event: Event = null;

  @Input()
  mAction: ActionInstance;

  @Input()
  mUniqueIndex: number

  /*
   * Event Variable: "mEvent"
   * This is the Event that is currently tied to the Rule / Action.  Contains possible
   * values for various information / settings.
   */
  @Input()
  set mEvent(newEvent: Event) {
    //Figure out if the Event is changing
    let eventChanging = false;
    const oldEvent = this._event;
    if (oldEvent == null && newEvent == null) {
      eventChanging = false;
    } else if (oldEvent == null || newEvent == null) {
      eventChanging = true;
    } else {
      eventChanging = oldEvent.id != newEvent.id;
    }

    //If the Event is changing, the "Objects of Interest" Options from the "Event" need to be reloaded
    if (eventChanging) {
      this._event = newEvent;
      this.refreshObjectsOfInterestOptions(true, true);
    }
  }
  get mEvent(): Event {
    return this._event;
  }

  mDialogNameData: InstanceData;

  mDialogs: Dialog[];

  mOriginalDialog: Dialog;
  mEditDialog = new Dialog();

  mSelectedButton: DialogButton = null;

  mChecklistItemName: string;

  mLastMessageValue: string;

  mSnackbarMessage: string = null;

  /*
   * Variables related to the "Choose Assets / People" Functionality for Map Options
   */
  //Holds the valid "Objects of Interest" Options that can be used from the Event (All "OBJECT_REFERENCE" Properties)
  mObjectsOfInterestOptions_FromEvent: ObjectOfInterestOption[] = [];
  //Holds the "Objects of Interest" Options for unknown values.  If the Dialog already has a value set, and it isn't part of the "Event" or "Assets / People" options, then it is unknown and needs a display so that it can be unselected if desired.
  mObjectsOfInterestOptions_Unknown: ObjectOfInterestOption[] = [];


  /*
   * Boolean Variable: "customMapOptions".
   * Indicates / Controls whether custom "Map Options" should be shown / used for the Dialog.
   */
  set customMapOptions(value: boolean) {
    if (this.mEditDialog != null) {
      if (value) {
        if (this.mEditDialog.mapOptions == null) {
          const newOptions = new MapOptions();
          newOptions.assets = true;
          newOptions.people = true;
          newOptions.perimeters = true;
          newOptions.breadcrumbs = false;
          newOptions.zones = false;
          newOptions.zonenames = false;
          newOptions.zoomToZone = false;
          newOptions.sublocations = false;
          newOptions.sublocationnames = false;
          this.mEditDialog.mapOptions = newOptions;
        }
      } else {
        if (this.mEditDialog.mapOptions != null) {
          this.mEditDialog.mapOptions = null;
        }
      }
    }
  }
  get customMapOptions(): boolean {
    return this.mEditDialog != null && this.mEditDialog.mapOptions != null;
  }





  constructor(
    private mRoute: ActivatedRoute,
    private mDialogService: DialogService,
    private mObjectService: ObjectService
  ) { }





  async ngOnInit(): Promise<void> {
    console.log("onInit() - Action: " + (this.mAction == null ? "*NULL*" : this.mAction.action == null ? "*NULL-ACTION*" : (this.mAction.action.id + "/" + this.mAction.action.name)) + ", Unique Index: " + this.mUniqueIndex);

    //Figure out/create the Instance Data for the "Dialog Name" property.  This is what the selected Dialog should be set to
    if (this.mAction.data == null) {
      this.mDialogNameData = new InstanceData();
      this.mDialogNameData.property = this.mAction.action.properties.find(prop => prop.property == PROP_DIALOGNAME);
      this.mAction.data = [ this.mDialogNameData ];
    } else {
      this.mDialogNameData = this.mAction.data.find(d => d.property.property == PROP_DIALOGNAME);
      if (this.mDialogNameData == null) {
        this.mDialogNameData = new InstanceData();
        this.mDialogNameData.property = this.mAction.action.properties.find(prop => prop.property == PROP_DIALOGNAME);
        this.mAction.data.push(this.mDialogNameData);
      }
    }

    this.mRoute.data.subscribe((data: {dialogs: Dialog[]}) => {
      //Get the available Dialogs
      this.mDialogs = data.dialogs == null ? [] : data.dialogs;

      //Select the Dialog
      if (this.mDialogNameData.value == null || this.mDialogNameData.value.length == 0) {
        this.selectDialogToEdit(null);
      } else {
        const dialog = this.mDialogs.find(d => d.name == this.mDialogNameData.value);
        this.selectDialogToEdit(dialog);
      }
    });
  }


  selectDialogToEdit(dialog: Dialog): void {
    console.log("selectDialogToEdit() - Dialog: " + (dialog == null ? "*NULL*" : (dialog.id + "/" + dialog.name)));
    this._selectDialogToEdit(dialog);
  }

  showMessageChanged(showMessage: boolean): void {
    if (!showMessage) {
      this.mLastMessageValue = this.mEditDialog.message;
      this.mEditDialog.message = null;
    } else if (this.mEditDialog.message == null) {
      this.mEditDialog.message = this.mLastMessageValue == null? "" : this.mLastMessageValue;
    }
  }

  showChecklistChanged(showChecklist: boolean): void {
    if (!showChecklist) {
      this.mEditDialog.items = null;
    } else if (this.mEditDialog.items == null) {
      this.mEditDialog.items = [];
    }
  }

  addChecklistItemClicked(): void {
    if (this.mChecklistItemName == null || this.mChecklistItemName.length == 0) {
      alert("You must write something!");
      return;
    } else {
      const item = new DialogItem();
      item.code = "GENERIC";
      item.label = this.mChecklistItemName;
      item.order = this.mEditDialog.items == null ? 1 : this.mEditDialog.items.length + 1;
      if (this.mEditDialog.items == null) {
        this.mEditDialog.items = [item];
      } else {
        this.mEditDialog.items.push(item);
      }
      this.mChecklistItemName = null;
      console.log("Added Checklist Item!  Label: '" + item.label + "', Order: '" + item.order + "'...");
    }
  }

  private async _selectDialogToEdit(dialog: Dialog): Promise<void> {
    if (dialog == null) {
      this.setDialog(dialog);
    } else {
      this.setDialog(await this.mDialogService.getDialogById(dialog.id));
    }
  }

  private setDialog(dialog: Dialog): void {
    //Set the Action's Dialog Name Property Data to the name of the Dialog that was selected.  This is how the Server references the Dialog
    this.mDialogNameData.value = dialog == null? null : dialog.name;
    this.mDialogNameData.object_value = dialog == null? null : dialog.name;

    if (dialog == null) {
      //Dialog is null, configure to create a new Dialog
      this.mOriginalDialog = null;
      this.mEditDialog = new Dialog();
      this.mEditDialog.level = Level.HIGH;

      //Set the value of the Message to null since there was no previous value.  This is used to recall a previously entered message when the User toggles "Show Message" off/on
      this.mLastMessageValue = null;

      //Reset the selected Button
      this.mSelectedButton = null;
    } else {
      //Dialog is not null, set it as the original Dialog, perform some handling to correct some things, then create a copy to be displayed/edited
      this.mOriginalDialog = dialog;

      //"Show Message" handling...
      if (this.mOriginalDialog != null && this.mOriginalDialog.message != null && this.mOriginalDialog.message.length == 0) {
        this.mOriginalDialog.message = null;
      }

      //Store the value of the Message so that it can be recalled if the "Show Message" is toggled off/on.
      this.mLastMessageValue = this.mOriginalDialog.message;

      //"Show Checklist" handling...
      if (this.mOriginalDialog.items != null && this.mOriginalDialog.items.length == 0) {
        this.mOriginalDialog.items = null;
      }

      //Fix the order of the buttons
      if (this.mOriginalDialog.buttons != null) {
        this.mOriginalDialog.buttons.sort((button1: DialogButton, button2: DialogButton) => {
          return button1.order - button2.order;
        });
      }

      //Create a copy of the Dialog which is the version that will be displayed/edited...
      this.mEditDialog = JSON.parse(JSON.stringify(this.mOriginalDialog));

      //Reset the selected Button
      this.mSelectedButton = null;
    }

    //Dialog changed, refresh the "Objects of Interest" Options, mainly looking for any value already added that doesn't have a valid option.
    this.refreshObjectsOfInterestOptions(false, true);
  }

  isNewDialog(): boolean {
    return this.mOriginalDialog == null;
  }

  isDialogChanged(): boolean {
    return !(
          this.mOriginalDialog.name == this.mEditDialog.name
      &&  this.mOriginalDialog.type == this.mEditDialog.type
      &&  !this._isRecipientsChanged()
      &&  this.mOriginalDialog.message == this.mEditDialog.message
      &&  this.mOriginalDialog.itemsHeader == this.mEditDialog.itemsHeader
      &&  !this._isChecklistItemsChanged()
      &&  this.mOriginalDialog.showMap == this.mEditDialog.showMap
      &&  this.mOriginalDialog.showComment == this.mEditDialog.showComment
      &&  this.mOriginalDialog.messageHeader == this.mEditDialog.messageHeader
      &&  !this._isButtonsChanged()
      &&  this.mOriginalDialog.responseTime == this.mEditDialog.responseTime
      &&  !this._isMapOptionsChanged()
    );
  }

  private _isRecipientsChanged(): boolean {
    if (  (this.mOriginalDialog.groups == null || this.mOriginalDialog.groups.length == 0)
      &&  (this.mEditDialog.groups == null || this.mEditDialog.groups.length == 0)
    ) {
      return false;
    } else if (this.mOriginalDialog.groups == null || this.mOriginalDialog.groups.length == 0 || this.mEditDialog.groups == null || this.mEditDialog.groups.length == 0) {
      return true;
    } else if (this.mOriginalDialog.groups.length != this.mEditDialog.groups.length) {
      return true;
    } else {
      for (const group of this.mOriginalDialog.groups) {
        const otherGroup = this.mEditDialog.groups.find(g => g.id == group.id);
        if (otherGroup == null) {
          return true;
        }
      }
      return false;
    }
  }

  private _isMapOptionsChanged(): boolean {
    if (this.mOriginalDialog.mapOptions != null && this.mEditDialog.mapOptions != null) {
      const optionsChanged = !(
        this.mOriginalDialog.mapOptions.assets == this.mEditDialog.mapOptions.assets &&
        this.mOriginalDialog.mapOptions.people == this.mEditDialog.mapOptions.people &&
        this.mOriginalDialog.mapOptions.breadcrumbs == this.mEditDialog.mapOptions.breadcrumbs &&
        this.mOriginalDialog.mapOptions.perimeters == this.mEditDialog.mapOptions.perimeters &&
        this.mOriginalDialog.mapOptions.zones == this.mEditDialog.mapOptions.zones &&
        this.mOriginalDialog.mapOptions.zonenames == this.mEditDialog.mapOptions.zonenames &&
        this.mOriginalDialog.mapOptions.sublocations == this.mEditDialog.mapOptions.sublocations &&
        this.mOriginalDialog.mapOptions.sublocationnames == this.mEditDialog.mapOptions.sublocationnames &&
        this.mOriginalDialog.mapOptions.zoomToZone == this.mEditDialog.mapOptions.zoomToZone
      );
      if (optionsChanged) {
        return true;
      } else {
        const oldPoi: string[] = this.mOriginalDialog.mapOptions.objectsOfInterest == null? [] : JSON.parse(this.mOriginalDialog.mapOptions.objectsOfInterest);
        const newPoi: string[] = this.mEditDialog.mapOptions.objectsOfInterest == null? [] : JSON.parse(this.mEditDialog.mapOptions.objectsOfInterest);
        //"Objects of Interest" Options can be considered unchanged if the lengths of the Old / New Values are the same and every value in the "Old" list is in the "New" list
        return oldPoi.length != newPoi.length || !oldPoi.every(oldP => newPoi.find(newP => newP == oldP) != null);
      }
    } else if (this.mOriginalDialog.mapOptions == null && this.mEditDialog.mapOptions == null) {
      return false;
    } else {
      return true;
    }
  }

  private _isChecklistItemsChanged(): boolean {
    if (  (this.mOriginalDialog.items == null || this.mOriginalDialog.items.length == 0)
      &&  (this.mEditDialog.items == null || this.mEditDialog.items.length == 0)
    ) {
      return false;
    } else if (this.mOriginalDialog.items == null || this.mOriginalDialog.items.length == 0 || this.mEditDialog.items == null || this.mEditDialog.items.length == 0) {
      return true;
    } else if (this.mOriginalDialog.items.length != this.mEditDialog.items.length) {
      return true;
    } else {
      let retVal = false;
      this.mEditDialog.items.forEach((item: DialogItem, index: number) => {
        if (item.id == null) {
          retVal = true;
        } else {
          const otherItem = this.mOriginalDialog.items[index];
          if (item.label != otherItem.label) {
            retVal = true;
          }
        }
      });
      return retVal;
    }
  }

  private _isButtonsChanged(): boolean {
    if (  (this.mOriginalDialog.buttons == null || this.mOriginalDialog.buttons.length == 0)
      &&  (this.mEditDialog.buttons == null || this.mEditDialog.buttons.length == 0)
    ) {
      return false;
    } else if (this.mOriginalDialog.buttons == null || this.mOriginalDialog.buttons.length == 0 || this.mEditDialog.buttons == null || this.mEditDialog.buttons.length == 0) {
      return true;
    } else if (this.mOriginalDialog.buttons.length != this.mEditDialog.buttons.length) {
      return true;
    } else {
      for (const button of this.mEditDialog.buttons) {
        if (button.id == null) {
          return true;
        } else {
          const otherButton = this.mOriginalDialog.buttons.find(b => b.id == button.id);
          if (otherButton == null) {
            return true;
          } else {
            if (  button.label != otherButton.label
              ||  button.code != otherButton.code
              ||  button.order != otherButton.order
              ||  button.type != otherButton.type
              ||  button.color.red != otherButton.color.red
            ) {
              return true;
            }
          }
        }
      }
      return false;
    }
  }


  createDialog(): void {
    console.log("createDialog()");
    this._createDialog();
  }

  private async _createDialog(): Promise<void> {
    //Attempt to save the Dialog
    let savedDialog: Dialog;
    try {
      savedDialog = await this.mDialogService.createDialog(this.mEditDialog);
    } catch (error) {
      this.handleError(error);
      return;
    }

    //Refresh the available Dialogs displayed in the drop down (need to add the newly created one to the list)
    this.mDialogs = await this.mDialogService.getAllDialogs();

    //Select the newly created Dialog as the one being displayed/edited
    this.selectDialogToEdit(savedDialog);

    //Display a message to the User informing them the Dialog was successfully saved
    this.showSnackbarMessage("Dialog created successfully!");
  }

  updateDialog(): void {
    console.log("updateDialog()");
    if (this.isDialogChanged()) {
      this._updateDialog();
    }
  }

  private async _updateDialog(): Promise<void> {
    //Attempt to update the Dialog
    let updatedDialog: Dialog;
    try {
      updatedDialog = await this.mDialogService.updateDialog(this.mEditDialog);
    } catch (error) {
      this.handleError(error);
      return;
    }

    //Refresh the available Dialogs displayed in the drop down (need to add the newly created one to the list)
    this.mDialogs = await this.mDialogService.getAllDialogs();

    //Select the newly created Dialog as the one being displayed/edited
    this.selectDialogToEdit(updatedDialog);

    //Display a message to the User informing them the Dialog was successfully updated
    this.showSnackbarMessage("Dialog updated successfully!");
  }

  duplicateDialog(): void {
    console.log("duplicateDialog()");
    this._duplicateDialog();
  }

  private async _duplicateDialog(): Promise<void> {
    //Create a copy of the dialog being edited... This will be the version that attempts to be created
    const newDialog = new Dialog();
    newDialog.level = this.mEditDialog.level;

    newDialog.name = this.mEditDialog.name;
    newDialog.type = this.mEditDialog.type;
    newDialog.groups = this.mEditDialog.groups;
    newDialog.message = this.mEditDialog.message;
    newDialog.itemsHeader = this.mEditDialog.itemsHeader;
    if (this.mEditDialog.items != null) {
      newDialog.items = [];
      this.mEditDialog.items.forEach((item: DialogItem, index: number) => {
        const newItem = new DialogItem();
        newItem.label = item.label;
        newItem.code = item.code;
        newItem.order = item.order;
        newDialog.items.push(newItem);
      });
    }
    newDialog.showMap = this.mEditDialog.showMap;
    newDialog.showComment = this.mEditDialog.showComment;
    newDialog.messageHeader = this.mEditDialog.messageHeader;
    if (this.mEditDialog.buttons != null) {
      newDialog.buttons = [];
      this.mEditDialog.buttons.forEach((button: DialogButton, index: number) => {
        const newButton = new DialogButton();
        newButton.label = button.label;
        newButton.code = button.code;
        newButton.color = button.color;
        newButton.type = button.type;
        newButton.order = button.order;
        newDialog.buttons.push(newButton);
      });
    }
    newDialog.responseTime = this.mEditDialog.responseTime;

    newDialog.mapOptions = this.mEditDialog.mapOptions;

    //Attempt to save the new Dialog
    let savedDialog: Dialog;
    try {
      savedDialog = await this.mDialogService.createDialog(newDialog);
    } catch (error) {
      this.handleError(error);
      return;
    }

    //Refresh the available Dialogs displayed in the drop down (need to add the newly created one to the list)
    this.mDialogs = await this.mDialogService.getAllDialogs();

    //Select the newly created Dialog as the one being displayed/edited
    this.selectDialogToEdit(savedDialog);

    //Display a message to the User informing them the Dialog was successfully saved
    this.showSnackbarMessage("Dialog created successfully!");
  }

  private showSnackbarMessage(message: string): void {
    this.mSnackbarMessage = message;

    const pointer = "#dialogMessageSnackbar_" + this.mUniqueIndex;
    $(pointer).show();
    setTimeout(() => {
      $(pointer).hide();
    }, 5000);
  }

  private handleError(error: any): void {
    console.error("handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }




  getObjectsOfInterestOptions(): ObjectOfInterestOption[] {
    const rv: ObjectOfInterestOption[] = [];
    if (this.mObjectsOfInterestOptions_FromEvent != null) {
      rv.push(...this.mObjectsOfInterestOptions_FromEvent);
    }
    if (this.mObjectsOfInterestOptions_Unknown != null) {
      rv.push(...this.mObjectsOfInterestOptions_Unknown);
    }
    return rv;
  }
  async refreshObjectsOfInterestOptions(refreshEventOptions: boolean, refreshUnknown: boolean): Promise<void> {
    //Event Properties as Options
    if (refreshEventOptions) {
      const options: ObjectOfInterestOption[] = [];
      if (this.mEvent != null && this.mEvent.properties != null) {
        for (const eventProperty of this.mEvent.properties) {
          if (eventProperty.type == PropertyType.OBJECT_REFERENCE) {
            const option = new ObjectOfInterestOption();
            option.mValue = "${event." + eventProperty.property + "}";
            option.mDisplayValue = "FROM EVENT => " + eventProperty.name;
            options.push(option);
          }
        }
      }
      this.mObjectsOfInterestOptions_FromEvent = options;
    }

    //Unknown
    if (refreshUnknown) {
      const options: ObjectOfInterestOption[] = [];
      if (this.mOriginalDialog != null && this.mOriginalDialog.mapOptions != null && this.mOriginalDialog.mapOptions.objectsOfInterest != null) {
        const values: string[] = JSON.parse(this.mOriginalDialog.mapOptions.objectsOfInterest);
        if (values != null) {
          for (const value of values) {
            let hasUi = false;
            if (value.startsWith("${")) {
              hasUi = this.mObjectsOfInterestOptions_FromEvent.find(option => option.mValue == value) != null;
            }
            if (!hasUi) {
              const option = new ObjectOfInterestOption();
              option.mValue = value;
              option.mDisplayValue = "UNKNOWN VALUE => " + value;
              options.push(option);
            }
          }
        }
      }
      this.mObjectsOfInterestOptions_Unknown = options;
    }
  }

  isObjectOfInterestSelected(option: ObjectOfInterestOption): boolean {
    if (option != null && this.mEditDialog != null && this.mEditDialog.mapOptions != null) {
      const value = this.mEditDialog.mapOptions.objectsOfInterest;
      if (value != null) {
        const values: string[] = JSON.parse(value);
        return values != null && values.length > 0 && values.find(val => val == option.mValue) != null;
      }
    }
    return false;
  }

  selectObjectOfInterest(option: ObjectOfInterestOption, select: boolean): void {
    if (option != null && this.mEditDialog != null) {
      if (select) {
        let values: string[];
        const value = this.mEditDialog.mapOptions.objectsOfInterest;
        if (value == null) {
          values = [ option.mValue ];
        } else {
          values = JSON.parse(value);
          values.push(option.mValue);
        }
        this.mEditDialog.mapOptions.objectsOfInterest = JSON.stringify(values);
      } else {
        const value = this.mEditDialog.mapOptions.objectsOfInterest;
        if (value != null) {
          const values: string[] = JSON.parse(value);
          if (values != null) {
            const index = values.indexOf(option.mValue);
            if (index > -1) {
              values.splice(index, 1);
              this.mEditDialog.mapOptions.objectsOfInterest = JSON.stringify(values);
            }
          }
        }
      }
    }
  }

}

class ObjectOfInterestOption {
  mValue: string;
  mDisplayValue: string;
}
